import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchRefundById,
  updateRefund,
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
  selectSelectedRefund,
  selectInitialFormData,
  selectRefundDetailStatus,
  selectRefundUpdateStatus,
  selectRefundDetailError,
  selectRefundUpdateError,
  selectRefundFormErrors,
} from '../../features/refunds/refundSlice';
import { selectUserInformations } from '../../features/authSlice';
import SharedForm from '../../components/forms/SharedForm';
import refundFields from '../../config/refundFields';
import { showToast } from '../../utils/utils';
import Loader from '../../components/Live/Loader';

/**
 * Edit Refund Page
 * Full page component for editing an existing refund
 */
const EditRefundPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedRefund = useSelector(selectSelectedRefund);
  const initialFormData = useSelector(selectInitialFormData);
  const detailStatus = useSelector(selectRefundDetailStatus);
  const updateStatus = useSelector(selectRefundUpdateStatus);
  const detailError = useSelector(selectRefundDetailError);
  const updateError = useSelector(selectRefundUpdateError);
  const formErrors = useSelector(selectRefundFormErrors);

  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchRefundById({ token: user.token, refundId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (selectedRefund) {
      setFormData(selectedRefund);
    }
  }, [selectedRefund]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    dispatch(updateFormData({ [name]: value }));

    if (formErrors[name]) {
      dispatch(clearFormErrors());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!initialFormData) {
      showToast('Error', 'Initial data not loaded', 'error');
      return;
    }

    dispatch(updateRefund({
      token: user.token,
      refundId: id,
      formData,
      initialData: initialFormData,
    }));
  };

  useEffect(() => {
    if (updateStatus === 'succeeded') {
      showToast('Success', 'Refund updated successfully', 'success');
      dispatch(resetSubmissionStatus());
      navigate(`/dashboard/refunds/${id}`, { replace: true });
    }
  }, [updateStatus, dispatch, navigate, id]);

  useEffect(() => {
    if (updateStatus === 'failed' && updateError) {
      showToast('Error', updateError, 'error');
    }
  }, [updateStatus, updateError]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/refunds', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading' || updateStatus === 'loading';

  if (detailStatus === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/refunds')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Refunds
          </button>
        </div>
      </div>
    );
  }

  if (!selectedRefund) {
    return null;
  }

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Edit Refund
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Update refund information below
              </p>
            </div>
            <button
              onClick={() => navigate(`/dashboard/refunds/${id}`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>

        {(updateError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {updateError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {updateError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <SharedForm
            fields={refundFields}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            entity="refund"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default EditRefundPage;
